<template>
  <div class="ai-chat-container">
    <!-- 左侧边栏 -->
    <div class="sidebar">
      <!-- AI Assistant 头部 -->
      <div class="sidebar-header">
        <div class="ai-assistant-info">
          <img
            src="@/assets/images/ai-icon.png"
            alt="AI Assistant"
            class="ai-avatar"
          />
          <span class="ai-title">AI Assistant</span>
        </div>
      </div>

      <!-- 功能切换菜单 -->
      <div class="sidebar-menu">
        <div
          class="menu-item"
          :class="{ active: activeTab === 'chat' }"
          @click="activeTab = 'chat'"
        >
          <div class="menu-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path
                d="M14 1a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1h-2.5L8 13.5 4.5 11H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h12zM2 2v8h3l2 2 2-2h3V2H2z"
              />
            </svg>
          </div>
          <span class="menu-text">Chat</span>
        </div>

        <!-- 暂时隐藏 AI Alert 菜单，等接口准备好后再启用 -->
        <!--
        <div
          class="menu-item"
          :class="{ active: activeTab === 'alert' }"
          @click="activeTab = 'alert'"
        >
          <div class="menu-icon">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <path
                d="M8 16A8 8 0 1 0 8 0a8 8 0 0 0 0 16zm.93-9.412-1 4.705c-.07.34.029.533.304.533.194 0 .487-.07.686-.246l-.088.416c-.287.346-.92.598-1.465.598-.703 0-1.002-.422-.808-1.319l.738-3.468c.064-.293.006-.399-.287-.47l-.451-.081.082-.381 2.29-.287zM8 5.5a1 1 0 1 1 0-2 1 1 0 0 1 0 2z"
              />
            </svg>
          </div>
          <span class="menu-text">AI Alert</span>
        </div>
        -->
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- Chat 内容 -->
      <div v-if="activeTab === 'chat'" class="chat-content">
        <div class="chat-messages">
          <!-- 默认欢迎消息 -->
          <div class="message ai-message">
            <div class="message-avatar">
              <img
                src="@/assets/images/ai-icon.png"
                alt="AI"
                class="ai-avatar-img"
              />
            </div>
            <div class="message-content">
              <div class="message-text">
                Hi {{ $store.state.itCode }}, what can I do for you ?
              </div>
            </div>
          </div>

          <!-- 动态消息列表 -->
          <div
            v-for="(message, index) in messages"
            :key="index"
            class="message"
            :class="message.type === 'user' ? 'user-message' : 'ai-message'"
          >
            <div class="message-avatar">
              <img
                v-if="message.type === 'ai'"
                src="@/assets/images/ai-icon.png"
                alt="AI"
                class="ai-avatar-img"
              />
              <svg
                v-else
                class="user-avatar-svg"
                width="32"
                height="32"
                viewBox="0 0 32 32"
                fill="none"
              >
                <circle cx="16" cy="16" r="16" fill="#f3f4f6" />
                <circle cx="16" cy="12" r="4" fill="#6b7280" />
                <path d="M8 24c0-4.4 3.6-8 8-8s8 3.6 8 8" fill="#6b7280" />
              </svg>
            </div>
            <div class="message-content">
              <div 
                class="message-text"
                :class="{ 'markdown-content': message.type === 'ai' }"
                v-if="message.type === 'ai'"
                v-html="renderMarkdown(message.content)"
              >
              </div>
              <div 
                class="message-text"
                v-else
              >
                {{ message.content }}
              </div>
              <span
                v-if="
                  message.type === 'ai' &&
                  index === messages.length - 1 &&
                  isTyping
                "
                class="typing-cursor"
              >
                |
              </span>
            </div>
          </div>

          <!-- 加载状态 -->
          <div v-if="isLoading" class="message ai-message">
            <div class="message-avatar">
              <img
                src="@/assets/images/ai-icon.png"
                alt="AI"
                class="ai-avatar-img"
              />
            </div>
            <div class="message-content">
              <div class="message-text loading-message">
                <div class="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="chat-input-area">
          <div class="input-container">
            <div class="input-wrapper">
              <textarea
                v-model="inputMessage"
                placeholder="Type your message here..."
                class="message-input"
                rows="1"
                :disabled="isLoading || isTyping"
                @keypress="handleKeyPress"
                @input="autoResize"
                ref="messageInput"
              ></textarea>
            </div>
            <button
              class="send-btn"
              @click="sendMessage"
              :disabled="!inputMessage.trim() || isLoading || isTyping"
              :class="{ loading: isLoading || isTyping }"
            >
              <svg
                v-if="!isLoading"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z"
                />
              </svg>
              <div v-else class="loading-spinner"></div>
            </button>
          </div>
        </div>
      </div>

      <!-- AI Alert 内容 -->
      <div v-else class="alert-content">
        <div class="alert-messages">
          <div class="alert-placeholder">
            <div class="alert-box">AI智能告警</div>
          </div>
        </div>

        <div class="alert-input-area">
          <div class="input-container">
            <div class="input-wrapper">
              <textarea
                v-model="alertInputMessage"
                placeholder="请输入查询内容（支持输入日期查询，支持输入客户查询）..."
                class="message-input"
                rows="1"
                :disabled="isAlertLoading"
                @keypress="handleAlertKeyPress"
                @input="autoResize"
                ref="alertMessageInput"
              ></textarea>
            </div>
            <button
              class="action-btn file-upload-btn"
              @click="triggerFileUpload"
              :disabled="isAlertLoading"
              title="上传文件"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M.5 9.9a.5.5 0 0 1 .5.5v2.5a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-2.5a.5.5 0 0 1 1 0v2.5a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2v-2.5a.5.5 0 0 1 .5-.5z"
                />
                <path
                  d="M7.646 1.146a.5.5 0 0 1 .708 0l3 3a.5.5 0 0 1-.708.708L8.5 2.707V11.5a.5.5 0 0 1-1 0V2.707L5.354 4.854a.5.5 0 1 1-.708-.708l3-3z"
                />
              </svg>
            </button>
            <input
              type="file"
              ref="fileInput"
              @change="handleFileUpload"
              multiple
              style="display: none"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv"
            />
            <button
              class="send-btn"
              @click="sendAlertMessage"
              :disabled="
                (!alertInputMessage.trim() && uploadedFiles.length === 0) ||
                isAlertLoading
              "
              :class="{ loading: isAlertLoading }"
            >
              <svg
                v-if="!isAlertLoading"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="currentColor"
              >
                <path
                  d="M15.964.686a.5.5 0 0 0-.65-.65L.767 5.855H.766l-.452.18a.5.5 0 0 0-.082.887l.41.26.001.002 4.995 3.178 3.178 4.995.002.002.26.41a.5.5 0 0 0 .886-.083l6-15Zm-1.833 1.89L6.637 10.07l-.215-.338a.5.5 0 0 0-.154-.154l-.338-.215 7.494-7.494 1.178-.471-.47 1.178Z"
                />
              </svg>
              <div v-else class="loading-spinner"></div>
            </button>
          </div>

          <!-- 上传文件列表 -->
          <div v-if="uploadedFiles.length > 0" class="uploaded-files">
            <div
              v-for="(file, index) in uploadedFiles"
              :key="index"
              class="file-item"
            >
              <div class="file-info">
                <svg
                  class="file-icon"
                  width="16"
                  height="16"
                  viewBox="0 0 16 16"
                  fill="currentColor"
                >
                  <path
                    d="M9.293 0H4a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V4.707A1 1 0 0 0 13.707 4L10 .293A1 1 0 0 0 9.293 0zM9.5 3.5v-2l3 3h-2a1 1 0 0 1-1-1zM4 2a1 1 0 0 1 1-1h3.5v2a2 2 0 0 0 2 2h2v7a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V2z"
                  />
                </svg>
                <span class="file-name">{{ file.name }}</span>
                <span class="file-size">{{ formatFileSize(file.size) }}</span>
              </div>
              <button
                class="remove-file-btn"
                @click="removeFile(index)"
                title="删除文件"
              >
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 12 12"
                  fill="currentColor"
                >
                  <path
                    d="M9.364 2.636a.5.5 0 0 1 0 .708L6.708 6l2.656 2.656a.5.5 0 0 1-.708.708L6 6.708l-2.656 2.656a.5.5 0 0 1-.708-.708L5.292 6 2.636 3.344a.5.5 0 1 1 .708-.708L6 5.292l2.656-2.656a.5.5 0 0 1 .708 0z"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import MarkdownIt from 'markdown-it'
import { aiChat } from './api'

export default {
  name: 'AiChat',
  data() {
    return {
      activeTab: 'chat', // 'chat' 或 'alert'
      inputMessage: '', // 用户输入的消息
      messages: [], // 聊天消息列表
      isLoading: false, // 是否正在加载AI回复
      alertInputMessage: '', // Alert页面用户输入的消息
      isAlertLoading: false, // Alert页面是否正在加载
      uploadedFiles: [], // 上传的文件列表
      isTyping: false, // 是否正在显示打字机效果
      typingTimer: null, // 打字机效果定时器
    }
  },
  created() {
    // 初始化 markdown-it
    this.md = new MarkdownIt({
      html: true, // 允许HTML标签
      breaks: true, // 转换换行符
      linkify: true, // 自动转换链接
      typographer: true, // 启用排版功能
    })
  },
  methods: {
    // 渲染 Markdown 内容
    renderMarkdown(content) {
      if (!content) return ''
      try {
        return this.md.render(content)
      } catch (error) {
        console.error('Markdown parsing error:', error)
        return content // 如果解析失败，返回原始内容
      }
    },
    // 发送消息
    async sendMessage() {
      if (!this.inputMessage.trim() || this.isLoading || this.isTyping) return

      const userMessage = this.inputMessage.trim()
      this.inputMessage = ''

      // 添加用户消息到聊天列表
      this.messages.push({
        type: 'user',
        content: userMessage,
        timestamp: new Date(),
      })

      // 设置加载状态
      this.isLoading = true

      try {
        // 调用AI接口
        const response = await aiChat({ question: userMessage })

        // 先添加一个空的 AI 消息到聊天列表
        this.messages.push({
          type: 'ai',
          content: '',
          timestamp: new Date(),
        })

        // 开始打字机效果
        this.startTypingEffect(response)
      } catch (error) {
        console.error('AI Chat Error:', error)
        this.messages.push({
          type: 'ai',
          content: 'Sorry, I encountered an error. Please try again.',
          timestamp: new Date(),
        })
      } finally {
        this.isLoading = false
        // 滚动到底部
        this.$nextTick(() => {
          this.scrollToBottom()
        })
      }
    },

    // 开始打字机效果
    startTypingEffect(fullText) {
      this.isTyping = true
      let currentText = ''
      let index = 0
      const messageIndex = this.messages.length - 1 // 最后一条消息的索引

      const typeNextChar = () => {
        if (index < fullText.length) {
          currentText += fullText[index]
          this.messages[messageIndex].content = currentText
          index++

          // 滚动到底部
          this.$nextTick(() => {
            this.scrollToBottom()
          })

          // 设置下一个字符的延迟，随机延迟让效果更自然
          const delay = Math.random() * 30 + 20 // 20-50ms 随机延迟
          this.typingTimer = setTimeout(typeNextChar, delay)
        } else {
          // 打字完成
          this.isTyping = false
          this.typingTimer = null
        }
      }

      // 开始打字
      typeNextChar()
    },

    // 滚动聊天区域到底部
    scrollToBottom() {
      const chatMessages = this.$el.querySelector('.chat-messages')
      if (chatMessages) {
        chatMessages.scrollTop = chatMessages.scrollHeight
      }
    },

    // 处理回车键发送消息
    handleKeyPress(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendMessage()
      }
    },

    // 自动调整文本框高度
    autoResize(event) {
      const textarea = event.target
      textarea.style.height = 'auto'
      textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px'
    },

    // 发送 Alert 消息
    async sendAlertMessage() {
      if (
        (!this.alertInputMessage.trim() && this.uploadedFiles.length === 0) ||
        this.isAlertLoading
      )
        return

      const alertMessage = this.alertInputMessage.trim()
      this.alertInputMessage = ''

      // 设置加载状态
      this.isAlertLoading = true

      try {
        // 这里暂时只处理输入，后续可以添加实际的API调用
        console.log('Alert Message:', alertMessage)
        console.log('Uploaded Files:', this.uploadedFiles)

        // 暂时模拟处理
        await new Promise(resolve => setTimeout(resolve, 1000))

        // 清空文件列表
        this.uploadedFiles = []
      } catch (error) {
        console.error('AI Alert Error:', error)
      } finally {
        this.isAlertLoading = false
      }
    },

    // 处理 Alert 回车键发送消息
    handleAlertKeyPress(event) {
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault()
        this.sendAlertMessage()
      }
    },

    // 触发文件上传
    triggerFileUpload() {
      this.$refs.fileInput.click()
    },

    // 处理文件上传
    handleFileUpload(event) {
      const files = Array.from(event.target.files)

      files.forEach(file => {
        // 检查文件大小 (10MB 限制)
        if (file.size > 10 * 1024 * 1024) {
          this.$message.warning(
            `文件 ${file.name} 大小超过 10MB，请选择较小的文件`,
          )
          return
        }

        // 检查是否已存在同名文件
        if (this.uploadedFiles.some(f => f.name === file.name)) {
          this.$message.warning(`文件 ${file.name} 已存在`)
          return
        }

        this.uploadedFiles.push(file)
      })

      // 清空文件输入框
      event.target.value = ''
    },

    // 删除文件
    removeFile(index) {
      this.uploadedFiles.splice(index, 1)
    },

    // 格式化文件大小
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
  },

  // 组件销毁时清理定时器
  beforeDestroy() {
    if (this.typingTimer) {
      clearTimeout(this.typingTimer)
      this.typingTimer = null
    }
  },
}
</script>

<style scoped lang="scss">
.ai-chat-container {
  display: flex;
  height: calc(100vh - 250px); // 减去顶部导航栏的高度
  background-color: #f5f5f5;
}

// 左侧边栏
.sidebar {
  width: 240px;
  background-color: #ffffff;
  border-right: 1px solid #f0f0f0;
  display: flex;
  flex-direction: column;

  .sidebar-header {
    padding: 24px 20px;
    border-bottom: 1px solid #f5f5f5;

    .ai-assistant-info {
      display: flex;
      align-items: center;
      gap: 12px;

      .ai-avatar {
        width: 32px;
        height: 32px;
        border-radius: 8px;
      }

      .ai-title {
        font-size: 16px;
        font-weight: 600;
        color: #1f2937;
      }
    }
  }

  .sidebar-menu {
    padding: 20px;

    .menu-item {
      display: flex;
      align-items: center;
      gap: 12px;
      padding: 12px 16px;
      margin-bottom: 4px;
      cursor: pointer;
      transition: all 0.15s ease;
      color: #6b7280;
      border-radius: 8px;
      font-weight: 500;

      &:hover {
        background-color: #fef2f2;
        color: #dc2626;
      }

      &.active {
        background: linear-gradient(46deg, #fe7301 0%, #e41e20 100%);
        color: #ffffff;
        box-shadow: 0 2px 8px rgba(254, 115, 1, 0.25);

        .menu-icon svg {
          color: #ffffff;
        }
      }

      .menu-icon {
        width: 16px;
        height: 16px;
        display: flex;
        align-items: center;
        justify-content: center;

        svg {
          transition: color 0.15s ease;
        }
      }

      .menu-text {
        font-size: 14px;
        font-weight: 500;
      }
    }
  }
}

// 主要内容区域
.main-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  background-color: #ffffff;
}

// Chat 内容样式
.chat-content {
  height: 100%;
  display: flex;
  flex-direction: column;

  .chat-messages {
    flex: 1;
    padding: 24px;
    overflow-y: auto;

    .message {
      display: flex;
      gap: 12px;
      margin-bottom: 20px;
      align-items: flex-start;

      .message-avatar {
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        margin-top: 2px; // 轻微向下偏移，让头像与文本第一行更好对齐

        .ai-avatar-img {
          width: 32px;
          height: 32px;
          border-radius: 8px;
        }

        .user-avatar-svg {
          border-radius: 50%;
        }
      }

      .message-content {
        flex: 1;
        max-width: 80%; // 增加消息内容最大宽度以适应表格等Markdown内容

        .message-text {
          background-color: #f8f9fa;
          border-radius: 12px;
          padding: 12px 16px;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          word-wrap: break-word;
          display: inline-block;
          width: auto;

          // Markdown 样式
          &.markdown-content {
            h1, h2, h3, h4, h5, h6 {
              margin: 16px 0 12px 0;
              font-weight: 600;
              line-height: 1.3;
              
              &:first-child {
                margin-top: 0;
              }
            }

            h1 { font-size: 18px; }
            h2 { font-size: 16px; }
            h3 { font-size: 15px; }
            h4, h5, h6 { font-size: 14px; }

            p {
              margin: 8px 0;
              
              &:first-child {
                margin-top: 0;
              }
              
              &:last-child {
                margin-bottom: 0;
              }
            }

            ul, ol {
              margin: 8px 0;
              padding-left: 20px;
              
              li {
                margin: 4px 0;
              }
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin: 12px 0;
              font-size: 13px;
              
              th, td {
                padding: 8px 12px;
                text-align: left;
                border: 1px solid #e1e5e9;
              }
              
              th {
                background-color: #f1f3f4;
                font-weight: 600;
                color: #444;
              }
              
              td {
                background-color: #fff;
              }
              
              tr:nth-child(even) td {
                background-color: #f8f9fa;
              }
              
              tr:hover td {
                background-color: #e8f4f8;
              }
            }

            code {
              background-color: #f1f3f4;
              padding: 2px 4px;
              border-radius: 3px;
              font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
              font-size: 13px;
              color: #d73a49;
            }

            pre {
              background-color: #f6f8fa;
              border: 1px solid #e1e5e9;
              border-radius: 6px;
              padding: 12px;
              margin: 12px 0;
              overflow-x: auto;
              
              code {
                background: none;
                padding: 0;
                color: #333;
                font-size: 12px;
                line-height: 1.4;
              }
            }

            blockquote {
              border-left: 4px solid #dfe2e5;
              padding: 0 12px;
              margin: 12px 0;
              color: #666;
              font-style: italic;
              
              p {
                margin: 8px 0;
              }
            }

            strong {
              font-weight: 600;
              color: #2c3e50;
            }

            em {
              font-style: italic;
              color: #555;
            }

            a {
              color: #1890ff;
              text-decoration: none;
              
              &:hover {
                text-decoration: underline;
              }
            }

            hr {
              border: none;
              border-top: 1px solid #e1e5e9;
              margin: 16px 0;
            }
          }
        }
      }

      &.ai-message {
        .message-content .message-text {
          background-color: #f0f7ff;
          border: 1px solid #e0f2fe;
        }
      }

      &.user-message {
        flex-direction: row-reverse;

        .message-content {
          display: flex;
          justify-content: flex-end;

          .message-text {
            background: linear-gradient(46deg, #fe7301 0%, #e41e20 100%);
            color: #ffffff;
          }
        }
      }
    }
  }

  .chat-input-area {
    padding: 16px 24px 24px;
    border-top: 1px solid #f0f0f0;

    .input-container {
      background-color: #f8f9fa;
      border-radius: 8px;
      padding: 12px 16px;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      min-height: 60px;

      .input-wrapper {
        flex: 1;

        .message-input {
          width: 100%;
          border: none;
          background: none;
          resize: none;
          outline: none;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          padding: 0; // 移除默认padding
          min-height: 20px;
          max-height: 120px;
          font-family: inherit;
          vertical-align: top; // 确保文本从顶部开始

          &::placeholder {
            color: #999;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      .input-actions {
        display: flex;
        gap: 8px;

        .action-btn {
          width: 32px;
          height: 32px;
          border: none;
          background: none;
          border-radius: 6px;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          color: #666;
          transition: all 0.2s ease;

          &:hover {
            background-color: #e9ecef;
            color: #333;
          }
        }
      }

      .send-btn {
        width: 32px;
        height: 32px;
        background: linear-gradient(46deg, #fe7301 0%, #e41e20 100%);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        transition: all 0.2s ease;
        flex-shrink: 0;
        align-self: flex-end;

        &:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(254, 115, 1, 0.3);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        &.loading {
          pointer-events: none;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      }
    }
  }

  // 加载状态样式
  .loading-message {
    .typing-indicator {
      display: flex;
      gap: 4px;
      align-items: center;

      span {
        width: 6px;
        height: 6px;
        background-color: #999;
        border-radius: 50%;
        animation: typing 1.4s infinite ease-in-out both;

        &:nth-child(1) {
          animation-delay: -0.32s;
        }
        &:nth-child(2) {
          animation-delay: -0.16s;
        }
        &:nth-child(3) {
          animation-delay: 0;
        }
      }
    }
  }
}

// AI Alert 内容样式
.alert-content {
  height: 100%;
  display: flex;
  flex-direction: column;

  .alert-messages {
    flex: 1;
    padding: 24px;
    display: flex;
    align-items: center;
    justify-content: center;

    .alert-placeholder {
      text-align: center;

      .alert-box {
        border: 2px dashed #d9d9d9;
        border-radius: 8px;
        padding: 40px 60px;
        color: #999;
        font-size: 16px;
        background-color: #fafafa;
      }
    }
  }

  .alert-input-area {
    padding: 16px 24px 24px;
    border-top: 1px solid #f0f0f0;

    .input-container {
      background-color: #f8f9fa;
      border-radius: 8px;
      padding: 12px 16px;
      display: flex;
      align-items: flex-start;
      gap: 12px;
      min-height: 60px;

      .input-wrapper {
        flex: 1;

        .message-input {
          width: 100%;
          border: none;
          background: none;
          resize: none;
          outline: none;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          padding: 0;
          min-height: 20px;
          max-height: 120px;
          font-family: inherit;
          vertical-align: top;

          &::placeholder {
            color: #999;
          }

          &:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }
        }
      }

      .action-btn,
      .file-upload-btn {
        width: 32px;
        height: 32px;
        border: none;
        background: none;
        border-radius: 6px;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: #666;
        transition: all 0.2s ease;
        flex-shrink: 0;
        align-self: flex-end;

        &:hover:not(:disabled) {
          background-color: #e9ecef;
          color: #333;
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
        }

        &.file-upload-btn {
          &:hover:not(:disabled) {
            background-color: #e8f5e8;
            color: #52c41a;
          }
        }
      }

      .send-btn {
        width: 32px;
        height: 32px;
        background: linear-gradient(46deg, #fe7301 0%, #e41e20 100%);
        border: none;
        border-radius: 50%;
        cursor: pointer;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        transition: all 0.2s ease;
        flex-shrink: 0;
        align-self: flex-end;

        &:hover:not(:disabled) {
          transform: scale(1.05);
          box-shadow: 0 2px 8px rgba(254, 115, 1, 0.3);
        }

        &:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        &.loading {
          pointer-events: none;
        }

        .loading-spinner {
          width: 16px;
          height: 16px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top: 2px solid white;
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
      }
    }

    .uploaded-files {
      margin-top: 12px;
      display: flex;
      flex-wrap: wrap;
      gap: 8px;

      .file-item {
        display: flex;
        align-items: center;
        gap: 8px;
        background-color: #f0f7ff;
        border: 1px solid #d1ecf1;
        border-radius: 6px;
        padding: 8px 12px;
        font-size: 12px;

        .file-info {
          display: flex;
          align-items: center;
          gap: 6px;
          flex: 1;

          .file-icon {
            color: #52c41a;
            flex-shrink: 0;
          }

          .file-name {
            color: #333;
            font-weight: 500;
            max-width: 120px;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
          }

          .file-size {
            color: #666;
            font-size: 11px;
          }
        }

        .remove-file-btn {
          background: none;
          border: none;
          cursor: pointer;
          color: #999;
          padding: 2px;
          border-radius: 2px;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
          justify-content: center;

          &:hover {
            background-color: #ffebee;
            color: #f44336;
          }
        }
      }
    }
  }
}

// 动画定义
@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes typing {
  0%,
  80%,
  100% {
    transform: scale(0.8);
    opacity: 0.5;
  }
  40% {
    transform: scale(1);
    opacity: 1;
  }
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

// 打字机光标样式
.typing-cursor {
  color: #fe7301;
  font-weight: bold;
  animation: blink 1s infinite;
  margin-left: 2px;
}
</style>
